import { For, Show, createSignal } from "solid-js";

// Stepper-style progression of lifecycle stages with click-to-advance.
// Renders each stage as a chip; the current one is `btn-primary`, prior
// ones get `btn-success` to convey momentum, later ones `btn-ghost`. Stages
// that are "terminal" (churned/lost/etc.) sit on the right and styled
// differently so they look like off-ramps, not next steps.

type Props = {
  stages: string[];
  current: string;
  // Mark certain stages as terminal/off-ramp (rendered with btn-error variant).
  terminalStages?: string[];
  // Called when the user clicks a stage to transition to it.
  onSelect: (stage: string) => Promise<void> | void;
  // Optional label override per stage (e.g. "MQL" for marketing_qualified).
  formatStage?: (stage: string) => string;
  disabled?: boolean;
};

const DEFAULT_FORMAT = (s: string) =>
  s
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

export function LifecycleProgress(props: Props) {
  const [busyStage, setBusyStage] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const format = () => props.formatStage ?? DEFAULT_FORMAT;
  const terminals = () => new Set(props.terminalStages ?? []);

  // Compute "ahead-of-current" relationships among non-terminal stages so the
  // colour ramp reflects progression along the main path.
  const linearStages = () => props.stages.filter((s) => !terminals().has(s));
  const currentIndex = () => linearStages().indexOf(props.current);

  const variantOf = (stage: string): string => {
    if (terminals().has(stage)) {
      return stage === props.current ? "btn-error" : "btn-ghost text-error";
    }
    const idx = linearStages().indexOf(stage);
    if (stage === props.current) return "btn-primary";
    if (currentIndex() === -1) return "btn-ghost";
    return idx < currentIndex() ? "btn-success btn-outline" : "btn-ghost";
  };

  const handleClick = async (stage: string) => {
    if (stage === props.current || props.disabled || busyStage()) return;
    setError(null);
    setBusyStage(stage);
    try {
      await props.onSelect(stage);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyStage(null);
    }
  };

  return (
    <div>
      <div role="group" aria-label="Lifecycle stages" class="flex flex-wrap gap-1">
        <For each={props.stages}>
          {(stage) => (
            <button
              type="button"
              class={`btn btn-sm ${variantOf(stage)}`}
              aria-current={stage === props.current ? "step" : undefined}
              aria-pressed={stage === props.current}
              disabled={props.disabled || busyStage() !== null}
              onClick={() => handleClick(stage)}
            >
              <Show
                when={busyStage() === stage}
                fallback={<>{format()(stage)}</>}
              >
                <span class="loading loading-spinner loading-xs" /> {format()(stage)}
              </Show>
            </button>
          )}
        </For>
      </div>
      <Show when={error()}>
        <div role="alert" class="text-error text-xs mt-1">
          {error()}
        </div>
      </Show>
    </div>
  );
}
